import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState, useContext, useRef } from 'react'
import { Breadcrumbs, Button, LinearProgress, ListItemButton, Typography } from '@mui/material'

import Dialog from '../components/common/dialog'
import '../styles/customer-details-management.css'
import { requestAccessToken } from '../api/auth.api'
import NextIcon from '../assets/icons/next-icon.svg'
import HelpIcon from '../assets/icons/help-icon.svg'
import { createBankAccount } from '../api/customers.api'
import { handleCheckIfBankAccountNumberIsValid, upsertManyWithLimitAtStartFast, upsertPaymentWithLimitAtStart } from '../utils'
import { Customer, BankAccount, FormError, Payment } from '../types'
import ActionsBar from '../components/customer-details-management/actions-bar'
import BankAccountForm from '../components/bank-accounts-management/bank-account-form'
import { AppStateContext, AuthContext, NotificationsContext, UserContext } from '../contexts'
import CustomerDetailsMainData from '../components/customer-details-management/customer-details-main-data'
import CustomerDetailsPaymentsListItem from '../components/customer-details-management/customer-details-payments-list-item'
import CustomerDetailsPaymentsBriefData from '../components/customer-details-management/customer-details-payments-brief-data'
import { handleUpdateDataByOne } from '../utils/db/common-methods'
import CustomerDetailsPaymentDetailsDataTable from '../components/customer-details-management/customer-details-payment-details-data-table'
import { getCustomerDetailsData } from '../api/app-data.api'
import { getCustomersDataWithSameBankAccount, searchPaymentsByRegex } from '../api/app-queries.api'
import { useSocket } from '../hooks/use-socket'
import { baseButtonBlueBg } from '../theme/custom-styles'
import { countriesCurrencies } from '../lib'

const CustomerDetailsManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

  const navigate = useNavigate()
  const { customerId } = useParams()
  const { userSession } = useContext(UserContext)
  const { banksData } = useContext(AppStateContext)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const { t } = useTranslation(['customer-details-management', 'payments-to-review-management', 'common', 'bank-accounts-management'])

  const customerDataRef = useRef<Customer | null>(null)
  const noRegistriesFoundAtDatabaseRef = useRef<boolean>(false)
  const customerPaymentsListDataRef = useRef<Array<Document> | null>(null)

  const [errors, setErrors] = useState<FormError>({})
  const [totalAmountPaid, setTotalAmountPaid] = useState<number>(0)
  const [paymentToSearch, setPaymentToSearch] = useState<string>('')
  const [totalPaymentsMade, setTotalPaymentsMade] = useState<number>(0)
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [customerData, setCustomerData] = useState<Partial<Customer>>({})
  const [searchedPayments, setSearchedPayments] = useState<Array<Payment>>([])
  const [searchingInDatabase, setSearchingInDatabase] = useState<boolean>(false)
  const [bankAccountData, setBankAccountData] = useState<Partial<BankAccount>>({})
  const [openPaymentDetailsModal, setOpenPaymentDetailsModal] = useState<boolean>(false)
  const [openNewBankAccountModal, setOpenNewBankAccountModal] = useState<boolean>(false)
  const [loadingPaymentsBriefData, setLoadingPaymentsBriefData] = useState<boolean>(true)
  const [totalPaymentsMadeThisMonth, setTotalPaymentsMadeThisMonth] = useState<number>(0)
  const [noRegistriesFoundAtLocal, setNoRegistriesFoundAtLocal] = useState<boolean>(false)
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState<Partial<Payment>>({})
  const [noRegistriesFoundAtDatabase, setNoRegistriesFoundAtDatabase] = useState<boolean>(false)
  const [dialogNewBankAccountIsLoading, setDialogNewBankAccountIsLoading] = useState<boolean>(false)
  const [customerPaymentsListData, setCustomerPaymentsListData] = useState<Array<Partial<Payment>>>([])
  const [customerMainBankAccountData, setCustomerMainBankAccountData] = useState<Partial<BankAccount>>({})

  useEffect(() => {
    if (!appDataSocket) return

    if (!userSession) return

    appDataSocket.on(`newPaymentAdded:${customerId}`, async (dataFromSocket: any) => {
      const localPaymentsByCustomerData = globalThis.sessionStorage.getItem(`customerDetailsData:${customerId}`) || JSON.stringify({ paymentsData: [] })
      const paymentsByCustomerDataParsed = JSON.parse(localPaymentsByCustomerData)
      const paymentsDataUpdated = upsertPaymentWithLimitAtStart(paymentsByCustomerDataParsed.paymentsData, dataFromSocket.paymentRequestedData, 500)

      const localCustomerDetailsData = globalThis.sessionStorage.getItem(`customerDetailsData:${customerId}`) || '{}'
      const customerDetailsData = JSON.parse(localCustomerDetailsData)

      handleSetInitialData({ ...customerDetailsData, customerPaymentsStats: dataFromSocket.customerPaymentsStats, paymentsData: paymentsDataUpdated })
      globalThis.sessionStorage.setItem(`customerDetailsData:${customerId}`, JSON.stringify({ ...customerDetailsData, customerPaymentsStats: dataFromSocket.customerPaymentsStats, paymentsData: paymentsDataUpdated }))
    })

    appDataSocket.on(`paymentUpdated:${customerId}`, async (dataFromSocket: any) => {
      if (dataFromSocket.paymentRequestedData.deleted === true) {
        handleDeletePaymentAndCalculate(dataFromSocket, String(dataFromSocket.paymentRequestedData._id))
        return
      }

      const localPaymentsByCustomerData = globalThis.sessionStorage.getItem(`customerDetailsData:${customerId}`) || JSON.stringify({ paymentsData: [] })
      const paymentsByCustomerDataParsed = JSON.parse(localPaymentsByCustomerData)
      const paymentsDataUpdated = upsertPaymentWithLimitAtStart(paymentsByCustomerDataParsed.paymentsData, dataFromSocket.paymentRequestedData, 500)

      const localCustomerDetailsData = globalThis.sessionStorage.getItem(`customerDetailsData:${customerId}`) || '{}'
      const customerDetailsData = JSON.parse(localCustomerDetailsData)

      handleSetInitialData({ ...customerDetailsData, customerPaymentsStats: dataFromSocket.customerPaymentsStats, paymentsData: paymentsDataUpdated })
      globalThis.sessionStorage.setItem(`customerDetailsData:${customerId}`, JSON.stringify({ ...customerDetailsData, customerPaymentsStats: dataFromSocket.customerPaymentsStats, paymentsData: paymentsDataUpdated }))
    })

    appDataSocket.on(`paymentDeleted:${customerId}`, async (dataFromSocket: any) => {
      handleDeletePaymentAndCalculate(dataFromSocket, dataFromSocket.paymentId)
    })

    appDataSocket.on(`customerUpdated:${customerId}`, async (dataFromSocket: any) => {
      if (dataFromSocket.deleted === false) {
        const customerDataUpdated = {
          ...customerDataRef.current,
          documentType: dataFromSocket.documentType,
          documentNumber: dataFromSocket.documentNumber,
          registeredFullName: dataFromSocket.registeredFullName
        }
        const mainBankAccountData = Array.isArray(dataFromSocket.bankAccountsData) ? dataFromSocket.bankAccountsData.find((bankAccountData: BankAccount) => bankAccountData.bankAccountMain === true) : undefined
        const bankAccountType = mainBankAccountData?.bankAccountType === '27' ? '1' : mainBankAccountData?.bankAccountType === '37' ? '2' : mainBankAccountData?.bankAccountType // --> Esto lo hacemos por el formato del cliente
        const mainBankAccountDataToDisplay = { ...mainBankAccountData }
        mainBankAccountDataToDisplay.bankAccountType = bankAccountType

        customerDataRef.current = customerDataUpdated
        setCustomerData(customerDataUpdated)
        setCustomerMainBankAccountData(mainBankAccountDataToDisplay || {})

        const localCustomerDetailsData = globalThis.sessionStorage.getItem(`customerDetailsData:${customerId}`) || '{}'
        const customerDetailsData = JSON.parse(localCustomerDetailsData)
        globalThis.sessionStorage.setItem(`customerDetailsData:${customerId}`, JSON.stringify({ ...customerDetailsData, customerData: customerDataUpdated }))
      }

      if (dataFromSocket.deleted === true) {
        navigate('/beneficiaries')
      }
    })

    appDataSocket.on(`customerDeleted:${customerId}`, async () => {
      navigate('/beneficiaries')
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, appDataSocket, isAppDataSocketInitialized])

  useEffect(() => {
    if (customerId) {
      handleGetCustomerDetailsData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId])

  const handleDeletePaymentAndCalculate = (dataFromSocket: any, paymentId: string) => {
    const localPaymentsByCustomerData = globalThis.sessionStorage.getItem(`customerDetailsData:${customerId}`) || JSON.stringify({ paymentsData: [] })
    const paymentsByCustomerDataParsed = JSON.parse(localPaymentsByCustomerData)
    const paymentsDataUpdated = paymentsByCustomerDataParsed.paymentsData.filter((paymentData: Payment) => String(paymentData._id) !== String(paymentId))

    const localCustomerDetailsData = globalThis.sessionStorage.getItem(`customerDetailsData:${customerId}`) || '{}'
    const customerDetailsData = JSON.parse(localCustomerDetailsData)

    handleSetInitialData({ ...customerDetailsData, customerPaymentsStats: dataFromSocket.customerPaymentsStats, paymentsData: paymentsDataUpdated })
    globalThis.sessionStorage.setItem(`customerDetailsData:${customerId}`, JSON.stringify({ ...customerDetailsData, customerPaymentsStats: dataFromSocket.customerPaymentsStats, paymentsData: paymentsDataUpdated }))
  }

  const handleGetCustomerDetailsData = async () => {
    let localCustomerDetailsDataToSet: any = {}
    const localCustomerDetailsData = globalThis.sessionStorage.getItem(`customerDetailsData:${customerId}`)

    if (localCustomerDetailsData) {
      localCustomerDetailsDataToSet = JSON.parse(localCustomerDetailsData)
      handleSetInitialData(localCustomerDetailsDataToSet)
    }

    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getCustomerDetailsData(String(customerId), accessToken)

      handleSetInitialData(data)
      globalThis.sessionStorage.setItem(`customerDetailsData:${customerId}`, JSON.stringify(data))
    } catch (error) {
      console.log('Error handleGetCustomerDetailsData > ', error)
    }
  }

  const handleSetInitialData = async (dataToSet: any) => {
    const customerCountry = dataToSet.customerData.country || 'COL'

    if (customerCountry.toUpperCase() !== selectedCountry) {
      navigate('/beneficiaries')
      return
    }

    const mainBankAccountData = Array.isArray(dataToSet.customerData.bankAccountsData)
      ? dataToSet.customerData.bankAccountsData.find((bankAccountData: BankAccount) => bankAccountData.bankAccountMain === true)
      : undefined
    const bankAccountType = mainBankAccountData?.bankAccountType === '27' ? '1' : mainBankAccountData?.bankAccountType === '37' ? '2' : mainBankAccountData?.bankAccountType // --> Esto lo hacemos por el formato del cliente
    const mainBankAccountDataToDisplay = { ...mainBankAccountData }
    mainBankAccountDataToDisplay.bankAccountType = bankAccountType

    // let calculateTotalAmountPaid = 0
    customerDataRef.current = dataToSet.customerData
    setCustomerData(dataToSet.customerData)
    setCustomerMainBankAccountData(mainBankAccountDataToDisplay || {})

    // for (const customerPayment of dataToSet.paymentsData) {
    //   calculateTotalAmountPaid += Number(customerPayment.amount)
    // }

    // const countPaymentsMadeThisMonth = countPaymentsThisMonth(dataToSet.paymentsData)
    setTotalPaymentsMade(dataToSet.customerPaymentsStats[0].totalCount)
    setTotalPaymentsMadeThisMonth(dataToSet.customerPaymentsStats[0].monthCount)
    setTotalAmountPaid(Number(dataToSet.customerPaymentsStats[0].totalAmount.$numberDecimal))

    customerPaymentsListDataRef.current = dataToSet.paymentsData
    setCustomerPaymentsListData(dataToSet.paymentsData)

    setLoadingPaymentsBriefData(false)
    setLoadingLocalData(false)
  }

  // const countPaymentsThisMonth = (payments: Array<Payment>) => {
  //   const nowTimezone = new Date(Date.now() - 5 * 60 * 60 * 1000)

  //   const startTimezone = new Date(nowTimezone.getFullYear(), nowTimezone.getMonth(), 1, 0, 0, 0, 0)

  //   const endTimezone = new Date(nowTimezone.getFullYear(), nowTimezone.getMonth() + 1, 0, 23, 59, 59, 999)

  //   return payments.filter((p) => {
  //     const createdTimezone = new Date(new Date(p.createdAt).getTime() - 5 * 60 * 60 * 1000)

  //     return createdTimezone >= startTimezone && createdTimezone <= endTimezone
  //   }).length
  // }

  const handleSetPaymentToSearch = (paymentToSearchData: string, showError: boolean) => {
    if (noRegistriesFoundAtDatabaseRef.current === true) {
      setNoRegistriesFoundAtDatabase(false)
    }

    setNoRegistriesFoundAtLocal(false)

    if (paymentToSearchData === '') {
      setSearchedPayments([])
      setPaymentToSearch(paymentToSearchData)

      if (showError) setErrors({ documentToSearch: t('searchField.errors.emptyField') })
      return
    }

    setPaymentToSearch(paymentToSearchData)
    const currentFullPaymentsData = [...customerPaymentsListData]
    const filteredPayments = currentFullPaymentsData.filter((payment) => {
      const paymentStatus =
        payment.status?.indexOf('error') !== -1
          ? t('paymentStatus.error')
          : payment.status === 'paid'
            ? t('paymentStatus.paid')
            : payment.status === 'processing'
              ? t('paymentStatus.processing')
              : payment.status === 'reviewed'
                ? t('paymentStatus.reviewed')
                : payment.status === 'rejected'
                  ? t('paymentStatus.rejected')
                  : t('paymentStatus.forReview')
      const bankAccountStatus = payment.bankAccountStatus === 'registered' ? t('paymentStatus.bankAccountStatus.registered') : t('paymentStatus.bankAccountStatus.["not-registered"]')

      return (
        paymentStatus.toLowerCase().includes(paymentToSearchData.toLowerCase()) ||
        String(payment.amount)?.toLowerCase().includes(paymentToSearchData.toLowerCase()) ||
        bankAccountStatus.toLowerCase().includes(paymentToSearchData.toLowerCase()) ||
        payment.description?.toLowerCase().includes(paymentToSearchData.toLowerCase()) ||
        payment.referenceNumber?.toLowerCase().includes(paymentToSearchData.toLowerCase()) ||
        payment.bankAccountNumber?.toLowerCase().includes(paymentToSearchData.toLowerCase())
      )
    }) as Array<Payment>

    setSearchedPayments(filteredPayments)

    if (filteredPayments.length === 0) {
      setNoRegistriesFoundAtLocal(true)
    }
  }

  const handleValidateNewBankAccountForm = async () => {
    setErrors({})
    let formDataIsValid = true
    const formErrors: FormError = {}
    const bankAccountAlreadyAdded = Array.isArray(customerData.bankAccountsData)
      ? customerData.bankAccountsData.find((bankAccountItemData: BankAccount) => bankAccountItemData.bankNumber === bankAccountData.bankNumber)
      : undefined

    if (!bankAccountData.bankAccountType) {
      formDataIsValid = false
      formErrors.bankAccountType = t('newBankAccountModal.bankAccountForm.errors.emptyBankAccountType', { ns: 'bank-accounts-management' })
    }

    if (selectedCountry === 'ARG' || selectedCountry === 'CHI' || selectedCountry === 'PER' || selectedCountry === 'BRA' || (selectedCountry === 'COL' && bankAccountData.bankAccountType !== 'breb')) {
      if (!bankAccountData.bankName) {
        formDataIsValid = false
        formErrors.bankName = t('newBankAccountModal.bankAccountForm.errors.emptyBankName', { ns: 'bank-accounts-management' })
      }
    }

    if (!bankAccountData.bankNumber) {
      formDataIsValid = false
      formErrors.bankAccountNumber = t('newBankAccountModal.bankAccountForm.errors.emptyBankAccountNumber', { ns: 'bank-accounts-management' })
    }

    if (bankAccountData.bankNumber && bankAccountAlreadyAdded) {
      formDataIsValid = false
      formErrors.bankAccountNumber = t('newBankAccountModal.bankAccountForm.errors.duplicateBankAccountNumber', { ns: 'bank-accounts-management' })
    }

    if (selectedCountry === 'COL' && bankAccountData.bankAccountType !== 'breb') {
      if (bankAccountData.bankNumber && isNaN(Number(bankAccountData.bankNumber.trim()))) {
        formDataIsValid = false
        formErrors.bankAccountNumber = t('bankAccountNoveltyForm.errorMessages.hasToBeOnlyDigits', { ns: 'payments-to-review-management' })
      }

      if (bankAccountData.bankNumber && !isNaN(Number(bankAccountData.bankNumber.trim()))) {
        const bankAccountNumberIsInvalid = handleCheckIfBankAccountNumberIsValid(bankAccountData as { bankAchCode: string }, bankAccountData.bankNumber.trim(), t)

        if (bankAccountNumberIsInvalid) {
          formDataIsValid = false
          formErrors.bankAccountNumber = bankAccountNumberIsInvalid
        }
      }
    }

    if (selectedCountry === 'BRA') {
      if (!bankAccountData.pix) {
        formDataIsValid = false
        formErrors.pixKey = t('newBankAccountModal.bankAccountForm.errors.emptyPixKey', { ns: 'bank-accounts-management' })
      }
    }

    if (!formDataIsValid) {
      return setErrors(formErrors)
    }

    setDialogNewBankAccountIsLoading(true)
    const {
      data: { accessToken }
    } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
    const { data } = await getCustomersDataWithSameBankAccount(String(userSession?.belongsTo), String(customerId), String(bankAccountData.bankNumber?.trim()), accessToken)

    if (Number.isNaN(Number(data)) || (!Number.isNaN(Number(data)) && data > 0)) {
      formDataIsValid = false
      formErrors.bankAccountNumber = t('bankAccountNoveltyForm.errorMessages.duplicateBankAccount', { ns: 'payments-to-review-management' })
    }

    if (!formDataIsValid) {
      setDialogNewBankAccountIsLoading(false)
      return setErrors(formErrors)
    }

    try {
      const bankAccountDataToCreate = {
        ...bankAccountData,
        bankAccountMain: Array.isArray(customerData.bankAccountsData) && customerData.bankAccountsData.length === 0 ? true : bankAccountData.bankAccountMain
      }
      const bankAccountCreated = await createBankAccount(bankAccountDataToCreate, accessToken, customerId)

      await handleUpdateDataByOne('customers', String(bankAccountCreated.data._id), bankAccountCreated.data)

      setBankAccountData({})
      setOpenNewBankAccountModal(false)
      setDialogNewBankAccountIsLoading(false)
      updateNewNotificationStatus?.(true, 'bank-account-created')
    } catch (error: any) {
      console.log('Error handleValidateNewCustomerForm', error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401 && error?.response?.data.message === 'Not authorized: Exists') {
        setErrors({ generalError: `${t('formGeneralErrors.unauthorized', { ns: 'common' })}: 406` })
      } else if (error?.response?.status === 401 && !(error?.response?.data.message === 'Not authorized: Exists')) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogNewBankAccountIsLoading(false)
    } finally {
      setDialogNewBankAccountIsLoading(false)
    }
  }

  const handleSearchPaymentsByCustomerDataByRegex = async () => {
    try {
      setErrors({})
      setSearchingInDatabase(true)

      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))

      const { data } = await searchPaymentsByRegex({ searchText: paymentToSearch, status: 'customer', customerId: String(customerId), belongsTo: userSession?.belongsTo, selectedCountry, selectedCurrency }, accessToken)

      if (data.paymentsData.length === 0) {
        setSearchingInDatabase(false)
        setNoRegistriesFoundAtDatabase(true)
        noRegistriesFoundAtDatabaseRef.current = true
        return
      }

      /* ESTO ES PARA ACTUALIZAR EL LISTADO SOLO CON LOS BUSCADOS */
      setSearchedPayments(data.paymentsData)
      /* ESTO ES PARA ACTUALIZAR EL LISTADO SOLO CON LOS BUSCADOS */

      const currentPaymentsData = [...(customerPaymentsListDataRef.current || [])]
      const paymentsDataUpdated = upsertManyWithLimitAtStartFast(currentPaymentsData, data.paymentsData, 1000)
      paymentsDataUpdated.sort((thisDocument: Partial<Payment>, nextDocument: Partial<Payment>) => {
        return Date.parse(String(nextDocument.createdAt)) - Date.parse(String(thisDocument.createdAt))
      })

      customerPaymentsListDataRef.current = paymentsDataUpdated
      setCustomerPaymentsListData(paymentsDataUpdated)

      setSearchingInDatabase(false)
      setNoRegistriesFoundAtLocal(false)

      const localCustomerDetailsData = globalThis.sessionStorage.getItem(`customerDetailsData:${customerId}`) || '{}'
      const customerDetailsData = JSON.parse(localCustomerDetailsData)
      globalThis.sessionStorage.setItem(`customerDetailsData:${customerId}`, JSON.stringify({ ...customerDetailsData, paymentsData: data.paymentsData }))
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setSearchingInDatabase(false)
      return
    }
  }

  const handleCloseSession = () => {
    if (removeAuth) {
      removeAuth()
      navigate('/', { replace: true, state: { sessionExpired: true } })
    }
  }

  return (
    <>
      <div className="section-container">
        <Breadcrumbs
          separator={
            <img
              src={NextIcon}
              alt="next-icon"
            />
          }
          sx={{ width: '100%' }}
          aria-label="breadcrumb"
        >
          <ListItemButton
            sx={{
              '&.MuiButtonBase-root': {
                padding: '0px'
              },
              '&.MuiButtonBase-root:hover': {
                backgroundColor: 'rgba(0,0,0,0)'
              }
            }}
            onClick={() => navigate('/beneficiaries')}
          >
            <Typography
              key="3"
              className="breadcrumb-inactive"
            >
              {t('breadcrumbs.firstLevel')}
            </Typography>
          </ListItemButton>
          <Typography
            key="3"
            className="breadcrumb-active"
          >
            {t('breadcrumbs.secondLevel')}
          </Typography>
        </Breadcrumbs>
        <div className="customer-details-profile-header">
          <CustomerDetailsMainData
            customerData={customerData}
            customerMainBankAccountData={customerMainBankAccountData}
            handleOpenNewBankAccountDialog={() => setOpenNewBankAccountModal(true)}
            handleGoToBanksAccountsManagement={() => navigate(`/beneficiary-details/${customerId}/banks-accounts`)}
          />
          {loadingPaymentsBriefData ? (
            <div className="customer-details-profile-payments-brief-data-loading"></div>
          ) : (
            <CustomerDetailsPaymentsBriefData
              totalAmountPaid={totalAmountPaid}
              totalPaymentsMade={totalPaymentsMade}
              totalPaymentsMadeThisMonth={totalPaymentsMadeThisMonth}
            />
          )}
        </div>
        <ActionsBar
          errors={errors}
          paymentToSearch={paymentToSearch}
          handleClearSearchInput={() => setErrors({})}
          handleSetPaymentToSearch={handleSetPaymentToSearch}
        />
        <div className="table-header-container">
          <div className="customer-details-list-item-left-data">
            <div className="customer-details-list-item-left-subdata table-header-text">{t('tableHeaders.date')}</div>
            <div className="customer-details-list-item-right-subdata table-header-text">{t('tableHeaders.amount')}</div>
          </div>
          <div className="customer-details-list-item-right-data">
            <div className="customer-details-list-item-left-subdata table-header-text">{t('tableHeaders.referenceNumber')}</div>
            <div className="customer-details-list-item-right-subdata table-header-text">{t('tableHeaders.status')}</div>
          </div>
        </div>
        {loadingLocalData ? (
          <div className="customer-details-list-container">
            <div className="documents-list-item-data-container-loader"></div>
            <div className="documents-list-item-data-container-loader"></div>
            <div className="documents-list-item-data-container-loader"></div>
            <div className="documents-list-item-data-container-loader"></div>
            <div className="documents-list-item-data-container-loader"></div>
          </div>
        ) : (
          <div className="customer-details-list-container">
            {((paymentToSearch === '' && customerPaymentsListData.length === 0) || (paymentToSearch !== '' && searchedPayments.length === 0)) && (
              <div className="customer-details-list-item-data-container">
                <div className="customer-details-list-item-no-data">{t('emptyPaymentListText')}</div>
              </div>
            )}
            {paymentToSearch !== '' && searchedPayments.length > 0 && (
              <>
                {searchedPayments.map((paymentData: Partial<Payment>) => {
                  return (
                    <CustomerDetailsPaymentsListItem
                      key={paymentData._id}
                      paymentData={paymentData}
                      handleOpenPaymentDetailsModal={(selectedPaymentData: Partial<Payment>) => {
                        setOpenPaymentDetailsModal(true)
                        setSelectedPaymentDetails(selectedPaymentData)
                      }}
                    />
                  )
                })}
              </>
            )}
            {paymentToSearch === '' && searchedPayments.length === 0 && (
              <>
                {customerPaymentsListData.map((paymentData: Partial<Payment>) => {
                  return (
                    <CustomerDetailsPaymentsListItem
                      key={paymentData._id}
                      paymentData={paymentData}
                      handleOpenPaymentDetailsModal={(selectedPaymentData: Partial<Payment>) => {
                        setOpenPaymentDetailsModal(true)
                        setSelectedPaymentDetails(selectedPaymentData)
                      }}
                    />
                  )
                })}
              </>
            )}
            {noRegistriesFoundAtLocal && (
              <div className="customer-no-data-found-container">
                {searchingInDatabase ? (
                  <>
                    <div className="customer-no-data-found-searching-progress-bar">
                      <LinearProgress />
                    </div>
                    <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case3', { ns: 'customer-company-details-management' })}</div>
                  </>
                ) : (
                  <>
                    {noRegistriesFoundAtDatabase === false ? (
                      <>
                        <div className="customer-no-data-found-no-registries-text">
                          {t('searchingResultTexts.case1.firstPart', { ns: 'customer-company-details-management' })}{' '}
                          <strong
                            className="customer-no-data-found-no-registries-text-click"
                            onClick={() => handleSearchPaymentsByCustomerDataByRegex()}
                          >
                            {t('searchingResultTexts.case1.thirdPart', { ns: 'customer-company-details-management' })}
                          </strong>{' '}
                          {t('searchingResultTexts.case1.fourthPart', { ns: 'customer-company-details-management' })}
                        </div>
                        <Button
                          sx={baseButtonBlueBg}
                          onClick={handleSearchPaymentsByCustomerDataByRegex}
                        >
                          {t('searchingResultTexts.expandSearch', { ns: 'customer-company-details-management' })}
                        </Button>
                        {errors.generalError && (
                          <div
                            style={{ marginTop: '0px' }}
                            className="error-message"
                          >
                            {errors.generalError}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="customer-no-data-found-no-registries-text">
                          <img
                            src={HelpIcon}
                            alt="help-icon"
                          />
                        </div>
                        <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case2', { ns: 'customer-company-details-management' })}</div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {openNewBankAccountModal && (
        <Dialog
          errors={errors}
          actionButtonText={t('newBankAccountModal.actionButtonText', { ns: 'bank-accounts-management' })}
          dialogTitle={t('newBankAccountModal.title', { ns: 'bank-accounts-management' })}
          dialogIsLoading={dialogNewBankAccountIsLoading}
          handleDialogAction={handleValidateNewBankAccountForm}
          handleDialogOpenState={(openState: boolean) => {
            setOpenNewBankAccountModal(openState)

            if (openState === false) {
              setErrors({})
              setBankAccountData({})
            }
          }}
        >
          <BankAccountForm
            errors={errors}
            banksData={banksData ?? []}
            bankAccountData={bankAccountData}
            handleSetBankAccountData={(bankAccountDataToAdd: BankAccount) => {
              setBankAccountData({ ...bankAccountDataToAdd })
            }}
            mainBankAccountAsDefault={Array.isArray(customerData.bankAccountsData) && customerData.bankAccountsData.length === 0}
          />
        </Dialog>
      )}
      {openPaymentDetailsModal && (
        <Dialog
          errors={errors}
          actionButtonText={t('paymentDetailsModal.actionButtonText')}
          dialogTitle={t('paymentDetailsModal.title')}
          handleDialogOpenState={(stateToSet) => setOpenPaymentDetailsModal(stateToSet)}
          handleDialogAction={() => {
            setSelectedPaymentDetails({})
            setOpenPaymentDetailsModal(false)
          }}
          hideActionsButtons={selectedPaymentDetails.status === 'reviewed' && selectedPaymentDetails.documentId !== ''}
        >
          <CustomerDetailsPaymentDetailsDataTable paymentDetailsData={selectedPaymentDetails} />
        </Dialog>
      )}
    </>
  )
}

export default CustomerDetailsManagement
